/**
 * WeekGrid — renders a single-week calendar grid with 7 day cells (Sun–Sat).
 * Provides expanded vertical space (minimum 10 cards before overflow) and
 * overlays DateSpan bars for multi-day cards.
 *
 * Validates: Requirements 9.1, 9.2, 9.6, 10.1, 10.2
 */

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { DayCell } from './DayCell';
import { DateSpan } from './DateSpan';
import { getWeekGrid } from '../../utils/date-utils';
import type { CardSummary, ListSummary } from '../../state/active-board-context';
import type { DateSpanData } from '../../utils/date-utils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface WeekGridProps {
  readonly weekStartDate: string; // ISO date of Sunday
  readonly cardsByDate: ReadonlyMap<string, readonly CardSummary[]>;
  readonly dateSpans: readonly DateSpanData[];
  readonly lists: readonly ListSummary[];
  readonly onDrop: (cardId: string, targetDate: string) => void;
  readonly onCardClick: (cardId: string) => void;
  readonly onNavigateWeek?: (delta: number) => void; // for Up/Down key wrapping
  /** Keyboard reschedule: move card left/right by one day */
  readonly onRescheduleMove?: (cardId: string, direction: 'left' | 'right') => void;
  /** Keyboard reschedule: confirm the move */
  readonly onRescheduleConfirm?: (cardId: string) => void;
  /** Keyboard reschedule: cancel the move */
  readonly onRescheduleCancel?: (cardId: string) => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DAY_HEADERS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
const MAX_VISIBLE = 10; // Expanded view per requirement 9.2

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function WeekGrid({
  weekStartDate,
  cardsByDate,
  dateSpans,
  lists,
  onDrop,
  onCardClick,
  onNavigateWeek,
  onRescheduleMove,
  onRescheduleConfirm,
  onRescheduleCancel,
}: WeekGridProps): React.ReactElement {
  const [dropTargetDate, setDropTargetDate] = useState<string | null>(null);
  const [focusedIndex, setFocusedIndex] = useState<number>(0);
  const cellRefs = useRef<(HTMLDivElement | null)[]>([]);

  const weekGrid = useMemo(() => getWeekGrid(weekStartDate), [weekStartDate]);

  // Compute visible date range for DateSpan clamping
  // getWeekGrid always returns exactly 7 cells
  const visibleStart = weekGrid.days[0]!.date;
  const visibleEnd = weekGrid.days[6]!.date;

  // Filter dateSpans to those overlapping this week
  const visibleSpans = useMemo(
    () =>
      dateSpans.filter(
        (span) => span.startDate <= visibleEnd && span.endDate >= visibleStart,
      ),
    [dateSpans, visibleStart, visibleEnd],
  );

  const handleCellDrop = useCallback(
    (date: string) => (cardId: string) => {
      onDrop(cardId, date);
      setDropTargetDate(null);
    },
    [onDrop],
  );

  const handleDragOver = useCallback(
    (date: string) => () => {
      setDropTargetDate(date);
    },
    [],
  );

  const handleDragLeave = useCallback(() => {
    setDropTargetDate(null);
  }, []);

  // Arrow key navigation handler for the grid
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      let newIndex = focusedIndex;

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          newIndex = focusedIndex - 1;
          if (newIndex < 0) {
            // Wrap to previous week
            onNavigateWeek?.(-1);
            return;
          }
          break;
        case 'ArrowRight':
          e.preventDefault();
          newIndex = focusedIndex + 1;
          if (newIndex > 6) {
            // Wrap to next week
            onNavigateWeek?.(1);
            return;
          }
          break;
        case 'ArrowUp':
          e.preventDefault();
          // Navigate to previous week
          onNavigateWeek?.(-1);
          return;
        case 'ArrowDown':
          e.preventDefault();
          // Navigate to next week
          onNavigateWeek?.(1);
          return;
        default:
          return;
      }

      setFocusedIndex(newIndex);
      cellRefs.current[newIndex]?.focus();
    },
    [focusedIndex, onNavigateWeek],
  );

  return (
    <div className="flex flex-col">
      {/* Day-of-week headers matching MonthGrid format */}
      <div className="grid grid-cols-7 border-b border-gray-200" role="row" aria-hidden="true">
        {DAY_HEADERS.map((day) => (
          <div
            key={day}
            className="px-2 py-1 text-xs font-medium text-gray-500 text-center"
          >
            {day}
          </div>
        ))}
      </div>

      {/* DateSpan overlay layer */}
      {visibleSpans.length > 0 && (
        <div className="grid grid-cols-7 gap-0 relative" aria-hidden="true">
          {visibleSpans.map((span) => (
            <DateSpan
              key={span.card.cardId}
              span={span}
              visibleStart={visibleStart}
              visibleEnd={visibleEnd}
              onClick={() => onCardClick(span.card.cardId)}
            />
          ))}
        </div>
      )}

      {/* ARIA grid container */}
      <div
        role="grid"
        aria-label="Week calendar grid"
        onKeyDown={handleKeyDown}
      >
        {/* Single row of 7 cells */}
        <div role="row" className="grid grid-cols-7">
          {weekGrid.days.map((cell, index) => (
            <div
              key={cell.date}
              ref={(el) => { cellRefs.current[index] = el; }}
              tabIndex={index === focusedIndex ? 0 : -1}
              onFocus={() => setFocusedIndex(index)}
              onDragOver={(e) => {
                e.preventDefault();
                handleDragOver(cell.date)();
              }}
              onDragLeave={handleDragLeave}
              className="min-h-[24rem]"
            >
              <DayCell
                date={cell.date}
                isCurrentMonth={cell.isCurrentMonth}
                isToday={cell.isToday}
                cards={cardsByDate.get(cell.date) ?? []}
                maxVisible={MAX_VISIBLE}
                lists={lists}
                isDropTarget={dropTargetDate === cell.date}
                onDrop={handleCellDrop(cell.date)}
                onCardClick={onCardClick}
                onRescheduleMove={onRescheduleMove}
                onRescheduleConfirm={onRescheduleConfirm}
                onRescheduleCancel={onRescheduleCancel}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
